import {
  createComponentKey,
  getComponentKeys,
  resolveComponentKeyById,
} from '@ben/columbia/api';
import Select from '@ben/orion/deprecated-select';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { memo, useCallback, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import type { ZodError } from 'zod';
import z from 'zod';

const newKeySchema = z
  .string()
  .toLowerCase()
  .trim()
  .max(250, { message: 'Key must be less than 250 characters' })
  .regex(/^[\d_a-z]+$/, {
    message:
      'Key must be lowercase and only contain numbers, characters and underscores.',
  });

type NewKey = z.infer<typeof newKeySchema>;

const DROPDOWN_QUERY_KEY = 'dropdownOptionsFor-target-user-input-keys';
const RESOLVE_QUERY_KEY = 'resolveComponentKeyById';

interface TargetUserInputKeySelectProps {
  name: `recalculationConfigs.${number}.targetUserInputKey`;
  validate?: (value: number | null | undefined) => true | string;
  onValueChange?: () => void;
}

const TargetUserInputKeySelect = ({
  name,
  validate,
  onValueChange,
}: TargetUserInputKeySelectProps) => {
  const { control } = useFormContext();
  const selectedId = useWatch({ name, exact: true }) as
    | number
    | null
    | undefined;

  const [localValidation, setLocalValidation] = useState<{
    hasError: boolean;
    message: string;
  }>({
    hasError: false,
    message: '',
  });
  const [searchKey, setSearchKey] = useState('');
  const [selectedLabel, setSelectedLabel] = useState<string | undefined>();

  const queryClient = useQueryClient();

  const { data: componentKeyOptions } = useQuery({
    queryKey: [DROPDOWN_QUERY_KEY, { searchKey }],
    queryFn: () => getComponentKeys(searchKey),
    select: (data: { results: { key: string; id: number }[] }) =>
      data.results.map(({ key, id }: { key: string; id: number }) => ({
        label: key,
        key: id.toString(),
      })),
  });

  const { data: selectedComponentKey } = useQuery({
    queryKey: [RESOLVE_QUERY_KEY, selectedId],
    queryFn: async () => {
      const componentKey = await resolveComponentKeyById(selectedId as number);
      return componentKey ?? null;
    },
    enabled: selectedId != null,
  });

  const componentKeys = useMemo(() => {
    const incomingResults = componentKeyOptions ?? [];

    if (
      selectedId == null ||
      incomingResults.some(({ key }) => key === selectedId.toString())
    ) {
      return incomingResults;
    }

    return [
      {
        label:
          selectedComponentKey?.key ?? selectedLabel ?? `Key ${selectedId}`,
        key: selectedId.toString(),
      },
      ...incomingResults,
    ];
  }, [componentKeyOptions, selectedComponentKey, selectedId, selectedLabel]);

  const createNewKeyMutation = useMutation({
    mutationFn: (newKey: NewKey) => createComponentKey(newKey),
    onError: (error: { response?: { data?: { key?: string[] } } }) => {
      toast.error(error?.response?.data?.key?.[0] || 'An error occurred');
    },
  });

  const handleSearchChange = debounce((search: string) => {
    setSearchKey(search);
    setLocalValidation({ hasError: false, message: '' });
  }, 100);

  const handleCreateOption = useCallback(
    async (newKey: NewKey, onChange: (value: number | null) => void) => {
      if (componentKeys?.find(({ label }) => label === newKey)) {
        toast.error('Component key already exists');
        return;
      }

      const validationResult = newKeySchema.safeParse(newKey);
      if (validationResult.success) {
        const normalizedKey = validationResult.data;
        const created = (await createNewKeyMutation.mutateAsync(
          normalizedKey
        )) as {
          id: number;
          key: string;
        };
        await queryClient.invalidateQueries([DROPDOWN_QUERY_KEY]);
        queryClient.setQueryData([RESOLVE_QUERY_KEY, created.id], created);
        setSelectedLabel(created.key);
        onChange(created.id);
        onValueChange?.();
        setLocalValidation({ hasError: false, message: '' });
        handleSearchChange('');
        return;
      }

      const error = validationResult.error as ZodError;
      setLocalValidation({
        hasError: true,
        message: error.issues[0].message,
      });
    },
    [componentKeys, createNewKeyMutation, onValueChange, queryClient]
  );

  const selectedValue = selectedId == null ? undefined : selectedId.toString();

  return (
    <Controller
      name={name}
      control={control}
      rules={{ validate }}
      render={({ field, fieldState }) => (
        <Select
          options={componentKeys || []}
          name="Target user input key"
          hintText="Component key whose enrolment user_input the minimum formula floors (e.g. ee_contribution). Leave blank with the formula to skip."
          onChange={(selected) => {
            if (selected) {
              const selectedOption = componentKeys?.find(
                ({ key }) => key === selected
              );
              setSelectedLabel(selectedOption?.label);
              field.onChange(Number(selected));
            } else {
              setSelectedLabel(undefined);
              field.onChange(null);
            }
            setLocalValidation({ hasError: false, message: '' });
            handleSearchChange('');
            onValueChange?.();
          }}
          onBlur={field.onBlur}
          hasCreationError={localValidation.hasError}
          hasError={!!fieldState.error || localValidation.hasError}
          errorMessage={fieldState.error?.message || localValidation.message}
          defaultValue={selectedValue}
          value={selectedValue}
          showSearch
          onSearchChange={handleSearchChange}
          canCreateOption
          onCreateOption={(newKey: string) =>
            void handleCreateOption(newKey, field.onChange)
          }
          clearable
          data-testid="target-user-input-key-select"
        />
      )}
    />
  );
};

export default memo(TargetUserInputKeySelect);
